import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { OTC_ENABLED } from '../constants/features';

const OFFSET_HOURS = 4;
const OFFSET_LABEL = 'UTC+04:00';

const formatOffsetTime = (now: Date): string => {
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const shifted = new Date(utcMs + OFFSET_HOURS * 60 * 60_000);
  const hh = String(shifted.getUTCHours()).padStart(2, '0');
  const mm = String(shifted.getUTCMinutes()).padStart(2, '0');
  const ss = String(shifted.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const useOffsetClock = (): string => {
  const [value, setValue] = useState<string>(() => formatOffsetTime(new Date()));
  useEffect(() => {
    const id = window.setInterval(() => setValue(formatOffsetTime(new Date())), 1000);
    return () => window.clearInterval(id);
  }, []);
  return value;
};

interface LayoutProps {
  children: React.ReactNode;
}

interface NavGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

interface NavItem {
  path: string;
  label: string;
  adminOnly?: boolean;
  permission?: string;
}

const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const P2PIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);

const OtcIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
  </svg>
);

const SwapIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 3v14m0 0-3-3m3 3 3-3M8 21V7m0 0-3 3m3-3 3 3" />
  </svg>
);

const WithdrawIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M5.25 6h13.5A2.25 2.25 0 0 1 21 8.25v9A2.25 2.25 0 0 1 18.75 19.5H5.25A2.25 2.25 0 0 1 3 17.25v-9A2.25 2.25 0 0 1 5.25 6Zm9.75 8.25h2.25" />
  </svg>
);

const FinanceIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
  </svg>
);

const WalletIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3" />
  </svg>
);

const AuditIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);

const SunIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
  </svg>
);

const MoonIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
  </svg>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { admin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const clock = useOffsetClock();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navGroups: NavGroup[] = [
    {
      key: 'users',
      label: 'Users',
      icon: <UsersIcon />,
      items: [
        { path: '/users', label: 'All Users', permission: 'users.view' },
        { path: '/user-activities', label: 'Activities', permission: 'users.view_activities' },
        { path: '/kyc', label: 'KYC', permission: 'kyc.view' },
        { path: '/bank-accounts', label: 'Bank Accounts', permission: 'kyc.view' },
      ],
    },
    {
      key: 'p2p',
      label: 'P2P',
      icon: <P2PIcon />,
      items: [
        { path: '/p2p', label: 'Trades', permission: 'p2p.view_trades' },
        { path: '/p2p/disputes', label: 'Disputes', permission: 'p2p.view_disputes' },
        { path: '/p2p/payment-methods', label: 'Payment Methods', permission: 'p2p.manage_payment_methods' },
        { path: '/p2p/rates', label: 'Fee Rates', permission: 'p2p.manage_rates' },
        { path: '/p2p/restrictions', label: 'Restrictions', permission: 'p2p.manage_restrictions' },
        { path: '/p2p/holding-period', label: 'Holding Period', permission: 'finance.view_operations' },
      ],
    },
    ...(OTC_ENABLED
      ? [
          {
            key: 'otc',
            label: 'OTC',
            icon: <OtcIcon />,
            items: [
              { path: '/otc', label: 'Trades', permission: 'p2p.view_trades' },
              { path: '/otc/disputes', label: 'Disputes', permission: 'p2p.view_disputes' },
              { path: '/otc/rates', label: 'Fee Rates', permission: 'p2p.manage_rates' },
              { path: '/otc/restrictions', label: 'Restrictions', permission: 'p2p.manage_restrictions' },
              { path: '/otc/holding-period', label: 'Holding Period', permission: 'finance.view_operations' },
              { path: '/otc/settings', label: 'Limits', permission: 'p2p.manage_settings' },
            ],
          },
        ]
      : []),
    {
      key: 'swap',
      label: 'Swap',
      icon: <SwapIcon />,
      items: [
        { path: '/swap', label: 'Operations', permission: 'swap.view_operations' },
        { path: '/swap/rates', label: 'Exchange Rates', permission: 'swap.view_operations' },
      ],
    },
    {
      key: 'withdrawal',
      label: 'Withdrawal',
      icon: <WithdrawIcon />,
      items: [
        { path: '/withdrawal', label: 'Review queue', permission: 'withdrawal.view_operations' },
        { path: '/withdrawal/rates', label: 'Fees & limits', permission: 'withdrawal.view_operations' },
      ],
    },
    {
      key: 'finance',
      label: 'Finance',
      icon: <FinanceIcon />,
      items: [
        { path: '/finance/operations/p2p', label: 'P2P', permission: 'finance.view_operations' },
        { path: '/finance/operations/swap', label: 'Swap', permission: 'finance.view_operations' },
        { path: '/finance/operations/withdrawals', label: 'Withdrawals', permission: 'finance.view_operations' },
        { path: '/finance/operations/deposits', label: 'Deposits', permission: 'finance.view_operations' },
        { path: '/finance/operations', label: 'All Operations', permission: 'finance.view_operations' },
        { path: '/finance/platform-rate', label: 'Platform Rate', permission: 'finance.view_operations' },
        { path: '/finance/platform-balance', label: 'Platform Balance', permission: 'finance.view_operations' },
      ],
    },
    {
      key: 'ledger',
      label: 'Ledger',
      icon: <WalletIcon />,
      items: [
        { path: '/ledger/balances', label: 'Account Balances', permission: 'ledger.view_wallets' },
        { path: '/ledger/wallets', label: 'Wallets', permission: 'ledger.view_wallets' },
        { path: '/withdrawal/hot-wallets', label: 'Hot wallets', permission: 'withdrawal.view_hot_wallets' },
        { path: '/ledger/deposits', label: 'Deposits', permission: 'ledger.view_deposits' },
      ],
    },
    {
      key: 'audit',
      label: 'System',
      icon: <AuditIcon />,
      items: [
        { path: '/audit-logs', label: 'Audit Logs', permission: 'audit.view' },
        { path: '/roles', label: 'Roles', permission: 'roles.view' },
        // Admin Access — hidden. Role assignment now lives inside Edit User on the Users page.
        // { path: '/roles/admins', label: 'Admin Access', permission: 'roles.assign' },
      ],
    },
  ];

  const isGroupActive = (group: NavGroup) =>
    group.items.some((item) => location.pathname === item.path);

  const hasPermission = (permission?: string) => {
    if (!permission) return true;
    if (admin?.role === 'super_admin') return true;
    return admin?.permissions?.includes(permission) ?? false;
  };

  const getVisibleItems = (group: NavGroup) =>
    group.items.filter((item) => hasPermission(item.permission));

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    navGroups.forEach((g) => {
      if (isGroupActive(g)) initial.add(g.key);
    });
    return initial;
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800/60 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center gap-3" onClick={() => setMobileSidebarOpen(false)}>
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">BO</span>
          </div>
          {sidebarOpen && <span className="text-lg font-semibold text-white whitespace-nowrap">Backoffice</span>}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {/* Dashboard - standalone */}
        <Link
          to="/dashboard"
          onClick={() => setMobileSidebarOpen(false)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
            location.pathname === '/dashboard'
              ? 'bg-indigo-500/15 text-indigo-400'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
          }`}
        >
          <DashboardIcon />
          {sidebarOpen && <span>Dashboard</span>}
        </Link>

        {sidebarOpen && <div className="pt-4 pb-1 px-3"><p className="text-[10px] font-semibold text-slate-600 uppercase tracking-widest">Manage</p></div>}
        {!sidebarOpen && <div className="pt-3" />}

        {/* Nav groups */}
        {navGroups.map((group) => {
          const visibleItems = getVisibleItems(group);
          if (visibleItems.length === 0) return null;

          const active = isGroupActive(group);
          const open = openGroups.has(group.key) || active;

          if (visibleItems.length === 1 && !sidebarOpen) {
            return (
              <Link
                key={group.key}
                to={visibleItems[0].path}
                onClick={() => setMobileSidebarOpen(false)}
                className={`flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  active
                    ? 'bg-indigo-500/15 text-indigo-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
                title={group.label}
              >
                {group.icon}
              </Link>
            );
          }

          return (
            <div key={group.key}>
              {sidebarOpen ? (
                <>
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                      active
                        ? 'text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                    }`}
                  >
                    <span className={active ? 'text-indigo-400' : ''}>{group.icon}</span>
                    <span className="flex-1 text-left">{group.label}</span>
                    <ChevronIcon open={open} />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="ml-5 pl-4 border-l border-slate-800 space-y-0.5 py-1">
                      {visibleItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileSidebarOpen(false)}
                          className={`block px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                            location.pathname === item.path
                              ? 'text-indigo-400 bg-indigo-500/10 font-medium'
                              : 'text-slate-500 hover:text-slate-200 hover:bg-slate-800/40'
                          }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => {
                    const firstItem = visibleItems[0];
                    if (firstItem) navigate(firstItem.path);
                  }}
                  className={`w-full flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                    active
                      ? 'bg-indigo-500/15 text-indigo-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                  }`}
                  title={group.label}
                >
                  {group.icon}
                </button>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section at bottom */}
      <div className="flex-shrink-0 border-t border-slate-800/60 p-3">
        {sidebarOpen ? (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-600/30 border border-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-indigo-300">
                {admin?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
              <p className="text-xs text-slate-500 truncate">{admin?.backoffice_role ?? admin?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-all"
              title="Logout"
            >
              <LogoutIcon />
            </button>
          </div>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center p-2.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-all"
            title="Logout"
          >
            <LogoutIcon />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen bg-slate-900/70 backdrop-blur-xl border-r border-slate-800/60 z-40 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-[68px]'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-screen w-64 bg-slate-900 border-r border-slate-800/60 z-50 transition-transform duration-300 ${
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-[68px]'}`}>
        {/* Top bar */}
        <header className="sticky top-0 h-14 bg-slate-900/50 backdrop-blur-xl border-b border-slate-800/40 z-30 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
            >
              <MenuIcon />
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${sidebarOpen ? '' : 'rotate-180'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono text-sm text-slate-200 tabular-nums">{clock}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                {OFFSET_LABEL}
              </span>
            </div>
            <div className="flex sm:hidden items-center gap-2 px-2 py-1 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <span className="font-mono text-xs text-slate-200 tabular-nums">{clock}</span>
              <span className="text-[9px] uppercase tracking-wider text-slate-500">+04</span>
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label="Toggle color theme"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <div className="hidden lg:flex flex-col items-end leading-tight">
              <p className="text-sm font-medium text-white">{admin?.name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                {admin?.backoffice_role ?? admin?.role}
              </p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1">
          <div className="p-4 lg:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
